import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Fish } from "lucide-react";
import { Button } from "../components/Button";
import { Field } from "../components/Field";
import { Input } from "../components/Input";
import { toDateTimeInput } from "../lib/format";

const CARP_TYPES = ["Mirror", "Common", "Leather", "Grass", "Ghost", "Koi", "Unknown"];

export default function LogCatchPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const requestedSessionId = params.get("sessionId") || "";
  const requestedAnglerId = params.get("anglerId") || "";
  const requestedRodNumber = params.get("rodNumber") || "";
  const requestedSetupId = params.get("setupId") || "";
  const quick = params.get("quick") === "1";

  const sessionsQuery = useQuery({
    queryKey: ["carpintel_sessions"],
    queryFn: () => fetch("/app-api/sessions").then((r) => (r.ok ? r.json() : [])),
  });
  const sessions: any[] = sessionsQuery.data ?? [];
  const [sessionId, setSessionId] = useState(requestedSessionId);
  const selectedSession = sessions.find((row) => String(row.id) === String(sessionId || requestedSessionId));
  const effectiveSessionId = String(selectedSession?.id ?? sessionId ?? "");

  const anglersQuery = useQuery({
    queryKey: ["carpintel_session_anglers", effectiveSessionId],
    queryFn: () => effectiveSessionId
      ? fetch(`/app-api/sessions/${effectiveSessionId}/anglers`).then((r) => (r.ok ? r.json() : []))
      : Promise.resolve([]),
    enabled: Boolean(effectiveSessionId),
  });
  const setupsQuery = useQuery({
    queryKey: ["carpintel_session_rod_setups", effectiveSessionId],
    queryFn: () => effectiveSessionId
      ? fetch(`/app-api/sessions/${effectiveSessionId}/rod-setups`).then((r) => (r.ok ? r.json() : []))
      : Promise.resolve([]),
    enabled: Boolean(effectiveSessionId),
  });
  const rodStateQuery = useQuery({
    queryKey: ["bankside_rod_state", effectiveSessionId],
    queryFn: () => effectiveSessionId
      ? fetch(`/app-api/sessions/${effectiveSessionId}/rod-state`).then((r) => (r.ok ? r.json() : { states: [], feed_events: [], positions: [] }))
      : Promise.resolve({ states: [], feed_events: [], positions: [] }),
    enabled: Boolean(effectiveSessionId),
  });

  const anglers: any[] = anglersQuery.data ?? [];
  const setups: any[] = setupsQuery.data ?? [];
  const states: any[] = rodStateQuery.data?.states ?? [];
  const positions: any[] = rodStateQuery.data?.positions ?? [];

  const [anglerId, setAnglerId] = useState(requestedAnglerId);
  const [rodNumber, setRodNumber] = useState(requestedRodNumber);
  const [caughtAt, setCaughtAt] = useState(toDateTimeInput(new Date().toISOString()));
  const [species, setSpecies] = useState("");
  const [carpType, setCarpType] = useState("Unknown");
  const [lb, setLb] = useState("");
  const [oz, setOz] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!sessionId && requestedSessionId) setSessionId(requestedSessionId);
  }, [requestedSessionId, sessionId]);
  useEffect(() => {
    if (requestedAnglerId) setAnglerId(requestedAnglerId);
    else if (anglers.length === 1) setAnglerId(String(anglers[0].id));
  }, [requestedAnglerId, anglers]);
  useEffect(() => {
    if (requestedRodNumber) setRodNumber(requestedRodNumber);
  }, [requestedRodNumber]);
  useEffect(() => {
    if (!species && selectedSession && ["carp", "catfish"].includes(String(selectedSession.target_species))) {
      setSpecies(String(selectedSession.target_species));
    }
  }, [selectedSession, species]);

  const selectedSetup = useMemo(() => {
    if (requestedSetupId) {
      const exact = setups.find((row) => String(row.id) === requestedSetupId);
      if (exact) return exact;
    }
    return setups.find((row) =>
      String(row.session_angler_id) === String(anglerId) && String(row.rod_number) === String(rodNumber),
    ) ?? null;
  }, [setups, requestedSetupId, anglerId, rodNumber]);

  const selectedPosition = useMemo(() => positions.find((row) =>
    String(row.session_angler_id) === String(anglerId) && String(row.rod_number) === String(rodNumber),
  ) ?? null, [positions, anglerId, rodNumber]);

  const liveSummary = useMemo(() => {
    const current = (component: string) => states.find((row) =>
      String(row.session_angler_id) === String(anglerId) &&
      String(row.rod_number) === String(rodNumber) &&
      row.component_type === component,
    );
    return {
      lead: current("lead_system")?.snapshot || selectedSetup?.lead_system_snapshot || "Not set",
      rig: current("rig")?.snapshot || selectedSetup?.rig_snapshot || "Not set",
      hookbait: current("hookbait")?.snapshot || selectedSetup?.hookbait_snapshot || "Not set",
      position: selectedPosition?.spot_snapshot || "Position not set",
    };
  }, [states, anglerId, rodNumber, selectedSetup, selectedPosition]);

  const rodOptions = useMemo(() => {
    const angler = anglers.find((row) => String(row.id) === String(anglerId));
    const count = Math.max(1, Number(angler?.rods_count || selectedSession?.rods_count || 1));
    return Array.from({ length: count }, (_, index) => index + 1);
  }, [anglers, anglerId, selectedSession?.rods_count]);

  async function saveCatch(event: React.FormEvent) {
    event.preventDefault();
    setMessage("");
    if (!effectiveSessionId || !selectedSession) { setMessage("Choose a session."); return; }
    if (!anglerId) { setMessage("Choose who caught the fish."); return; }
    if (!rodNumber) { setMessage("Choose the rod."); return; }
    if (!species) { setMessage("Choose the species caught."); return; }
    if (!caughtAt || Number.isNaN(new Date(caughtAt).getTime())) { setMessage("Choose a valid catch time."); return; }

    setSaving(true);
    try {
      const weightEntered = lb.trim() !== "" || oz.trim() !== "";
      const legacySpecies = species === "catfish" ? "Catfish" : "Carp";
      const payload = {
        session_id: Number(effectiveSessionId),
        session_angler_id: Number(anglerId),
        catch_species: species,
        species: legacySpecies,
        carp_type: species === "carp" ? carpType : "Unknown",
        caught_at: new Date(caughtAt).toISOString(),
        weight_lb: weightEntered ? Number(lb || 0) : null,
        weight_oz: weightEntered ? Number(oz || 0) : null,
        rod_number: Number(rodNumber),
        rod_setup_id: selectedSetup?.id ? Number(selectedSetup.id) : null,
        peg: selectedSession.peg || "",
        spot: selectedPosition?.spot_snapshot || "",
        spot_id: selectedPosition?.spot_id ? Number(selectedPosition.spot_id) : null,
        distance_m: selectedPosition?.distance_m ?? null,
        depth_ft: selectedPosition?.depth_ft ?? null,
        canonical_rig_id: selectedSetup?.canonical_rig_id ?? null,
        user_rig_id: selectedSetup?.user_rig_id ?? null,
        rig_snapshot: selectedSetup?.rig_snapshot ?? null,
        rig: selectedSetup?.rig_snapshot ?? "",
        canonical_feed_bait_id: selectedSetup?.canonical_feed_bait_id ?? null,
        user_feed_bait_id: selectedSetup?.user_feed_bait_id ?? null,
        feed_bait_snapshot: selectedSetup?.feed_bait_snapshot ?? null,
        main_bait: selectedSetup?.feed_bait_snapshot ?? "",
        canonical_hookbait_id: selectedSetup?.canonical_hookbait_id ?? null,
        user_hookbait_id: selectedSetup?.user_hookbait_id ?? null,
        hookbait_snapshot: selectedSetup?.hookbait_snapshot ?? null,
        hookbait: selectedSetup?.hookbait_snapshot ?? "",
        canonical_additive_bait_id: selectedSetup?.canonical_additive_bait_id ?? null,
        user_additive_bait_id: selectedSetup?.user_additive_bait_id ?? null,
        additive_snapshot: selectedSetup?.additive_snapshot ?? null,
        additive: selectedSetup?.additive_snapshot ?? "",
        baiting_approach: selectedSetup?.baiting_approach ?? null,
        bait_amount: selectedSetup?.bait_amount ?? null,
        bait_unit: selectedSetup?.bait_unit ?? "grams",
        bait_notes: "",
        privacy_setting: selectedSession.privacy_setting || "Contribute anonymously to CarpIntel statistics",
        share_peg_anonymously: selectedSession.share_peg_anonymously === true,
        catch_notes: notes.trim(),
        photo_url: null,
      };

      const response = await fetch("/app-api/catches", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || !result.id) throw new Error(result.error || "Could not save catch");

      if (photo) {
        try {
          const data = new FormData(); data.append("file", photo);
          const upload = await fetch("/app-api/photos", { method: "POST", body: data });
          if (upload.ok) {
            const uploaded = await upload.json();
            if (uploaded.url) await fetch(`/app-api/catches/${result.id}`, {
              method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ photo_url: uploaded.url }),
            });
          }
        } catch {
          // The catch is already safely stored. Photo upload stays secondary.
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["carpintel_catches"] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_stats"] }),
        queryClient.invalidateQueries({ queryKey: ["catchcompass_rig_intelligence"] }),
        queryClient.invalidateQueries({ queryKey: ["catchcompass_tackle_intelligence"] }),
        queryClient.invalidateQueries({ queryKey: ["catchcompass_position_intelligence"] }),
      ]);

      if (quick) {
        navigate(`/live/${effectiveSessionId}?caught=1&rod=${encodeURIComponent(rodNumber)}&angler=${encodeURIComponent(anglerId)}`);
      } else {
        navigate(`/catches/${result.id}`);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not save catch");
      setSaving(false);
    }
  }

  if (sessionsQuery.isLoading) return <div className="mx-auto max-w-[760px] px-4 py-8 text-sm text-secondary">Loading your session…</div>;
  if (!sessions.length) return <div className="mx-auto max-w-[760px] space-y-4 px-4 py-8"><p className="text-sm text-secondary">Start fishing before logging a catch.</p><Button asChild><Link to="/scout">Start Fishing</Link></Button></div>;

  return (
    <div className="mx-auto max-w-[760px] space-y-5 px-4 py-6 pb-28">
      <header>
        <Link to={effectiveSessionId && !String(effectiveSessionId).startsWith("offline-") ? `/live/${effectiveSessionId}` : "/app"} className="text-sm font-medium text-accent">← {quick ? "Live session" : "Back"}</Link>
        <div className="mt-3 flex items-start gap-3"><Fish className="mt-1 shrink-0 text-accent" /><div><h1 className="text-2xl font-semibold text-primary">Log catch</h1><p className="mt-1 text-sm leading-6 text-secondary">Tell CatchCompass about the fish. The rod already knows its position, lead system, rig, hookbait and feed state.</p></div></div>
      </header>

      <form onSubmit={saveCatch} className="space-y-5">
        {!requestedSessionId ? <section className="space-y-3 rounded-xl border border-border bg-raised p-4"><Field label="Session" id="catch-session"><select id="catch-session" value={effectiveSessionId} onChange={(e) => { setSessionId(e.target.value); setAnglerId(""); setRodNumber(""); }} className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary"><option value="">Choose session</option>{sessions.map((row) => <option key={row.id} value={row.id}>{row.lake_name || "Session"}{row.peg ? ` · ${row.peg}` : ""}</option>)}</select></Field></section> : null}

        <section className="space-y-4 rounded-xl border border-border bg-raised p-4">
          <h2 className="font-semibold text-primary">Fish</h2>
          {anglers.length > 1 ? <Field label="Caught by" id="catch-angler"><select id="catch-angler" value={anglerId} onChange={(e) => { setAnglerId(e.target.value); setRodNumber(""); }} className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary"><option value="">Choose angler</option>{anglers.map((row) => <option key={row.id} value={row.id}>{row.display_name}</option>)}</select></Field> : null}
          <Field label="Rod" id="catch-rod"><div className="grid grid-cols-3 gap-2">{rodOptions.map((rod) => <button key={rod} type="button" onClick={() => setRodNumber(String(rod))} className={`min-h-11 rounded-lg border px-3 py-2 text-sm font-semibold ${String(rod) === String(rodNumber) ? "border-accent bg-accent text-white" : "border-border bg-background text-primary"}`}>Rod {rod}</button>)}</div></Field>
          <Field label="Species caught" id="catch-species"><div className="grid grid-cols-2 gap-2">{(["carp", "catfish"] as const).map((value) => <button key={value} type="button" onClick={() => setSpecies(value)} className={`min-h-11 rounded-lg border px-3 py-2 text-sm font-semibold capitalize ${species === value ? "border-accent bg-accent text-white" : "border-border bg-background text-primary"}`}>{value}</button>)}</div></Field>
          {species === "carp" ? <Field label="Carp type" id="carp-type"><select id="carp-type" value={carpType} onChange={(e) => setCarpType(e.target.value)} className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary">{CARP_TYPES.map((type) => <option key={type}>{type}</option>)}</select></Field> : null}
          <div className="grid grid-cols-2 gap-3"><Field label="Weight lb" id="catch-lb"><Input id="catch-lb" type="number" min="0" value={lb} onChange={(e) => setLb(e.target.value)} placeholder="Optional" /></Field><Field label="Weight oz" id="catch-oz"><Input id="catch-oz" type="number" min="0" max="15" value={oz} onChange={(e) => setOz(e.target.value)} placeholder="0" /></Field></div>
          <Field label="Catch time" id="catch-time"><Input id="catch-time" type="datetime-local" value={caughtAt} onChange={(e) => setCaughtAt(e.target.value)} /></Field>
          <Field label="Notes" id="catch-notes"><textarea id="catch-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-20 w-full rounded-lg border border-border bg-background p-3 text-sm text-primary" placeholder="Optional" /></Field>
          <label className="flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 text-sm font-semibold text-primary"><Camera className="size-4" /> {photo ? photo.name : "Add photo (optional)"}<input className="sr-only" type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] || null)} /></label>
        </section>

        {rodNumber ? <section className="rounded-xl border border-accent/30 bg-accent/5 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-accent">Inherited from Rod {rodNumber}</p><div className="mt-3 grid grid-cols-2 gap-3 text-sm"><Summary label="Position" value={liveSummary.position} /><Summary label="Lead" value={liveSummary.lead} /><Summary label="Rig" value={liveSummary.rig} /><Summary label="Hookbait" value={liveSummary.hookbait} /></div><p className="mt-3 text-xs leading-5 text-secondary">You do not need to re-enter any of this. CatchCompass stores the state that was active at the catch time, then asks what changed after the fish goes back.</p></section> : null}

        {message ? <p className="rounded-lg border border-error bg-error-weak p-3 text-sm text-error" role="alert">{message}</p> : null}
        <Button type="submit" size="lg" className="w-full" isLoading={saving}>Save catch</Button>
      </form>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0"><p className="text-xs text-secondary">{label}</p><p className="mt-1 truncate font-semibold text-primary">{value}</p></div>;
}
